import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface FeedArtist {
  id: string;
  name: string;
  genre: string;
  fans: string;
  img: string;
  upcomingEvents: number;
}

export interface FeedConcert {
  id: string;
  artist: string;
  artistId: string;
  artistImage: string;
  venue: string;
  city: string;
  date: string;
  price: string;
  image: string;
  url: string;
}

interface FeedData {
  artists: FeedArtist[];
  concerts: FeedConcert[];
  personalized: boolean;
  loading: boolean;
}

const FeedContext = createContext<FeedData>({
  artists: [],
  concerts: [],
  personalized: false,
  loading: true,
});

export const usePersonalizedFeed = () => useContext(FeedContext);

export const PersonalizedFeedProvider = ({ children }: { children: ReactNode }) => {
  const { session } = useAuth();
  const [data, setData] = useState<FeedData>({
    artists: [],
    concerts: [],
    personalized: false,
    loading: true,
  });

  useEffect(() => {
    const fetchFeed = async () => {
      setData((prev) => ({ ...prev, loading: true }));
      try {
        const { data: result, error } = await supabase.functions.invoke("personalized-feed");
        if (error) throw error;
        if (result?.error) throw new Error(result.error);
        setData({
          artists: result.artists || [],
          concerts: result.concerts || [],
          personalized: result.personalized || false,
          loading: false,
        });
      } catch (e: any) {
        console.warn("Feed fetch error:", e);
        setData((prev) => ({ ...prev, loading: false }));
      }
    };

    fetchFeed();
  }, [session?.access_token]);

  return <FeedContext.Provider value={data}>{children}</FeedContext.Provider>;
};
